import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { AppButton } from '../../../components/UI';
import { ConfirmDialog, ErrorBanner, IconBox, LinkedinBlockedHelp, Panel, PanelHeader, PipelineSteps } from './ui';
import LinkedinAccount from './LinkedinAccount';
import CandidateBrowser from './CandidateBrowser';
import AddToCrmDialog from './AddToCrmDialog';
import { checkCandidatDuplicate } from '../../../services/CandidatDuplicateService';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import {
  addToCrm, deleteCandidate, fetchAllCandidates, fetchCandidates, isLinkedinBlocked, retryScraping, retrySmart, retryStorage, subscribeEvents,
} from '../api';
import {
  SMART_RETRY_REASONS, candidateName, candidateUrl, hasLinkedinErrorNoise, readCrmLinks, saveCrmLink, toCrmForm,
} from '../constants';
import { useLinkedin } from '../extensionBridge';

/** With the extension, the LinkedIn pages are opened in this browser during the import. */
const BrowserCollectionHint = () => {
  const li = useLinkedin();
  if (!li?.ready) return null;
  return (
    <Typography sx={{ fontSize: '0.76rem', color: '#64748b', mt: -1 }}>
      Pendant l'import, une fenêtre LinkedIn s'ouvre en arrière-plan dans ce navigateur : laissez-la ouverte (non réduite)
      et gardez cette page Sourcing ouverte jusqu'à la fin.
    </Typography>
  );
};


/** CSV drop zone + limit, shared by the candidate and mission imports. */
export const CsvUpload = ({ title, hint, limitLabel, defaultLimit = 3, launchLabel, disabled, onLaunch, children }) => {
  const [file, setFile] = useState(null);
  const [limit, setLimit] = useState(defaultLimit);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const accept = (f) => {
    if (f && f.name.toLowerCase().endsWith('.csv')) setFile(f);
  };

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!disabled) accept(e.dataTransfer.files?.[0]); }}
        sx={{
          p: 3.5,
          borderRadius: '12px',
          border: '1.5px dashed',
          borderColor: dragOver ? '#03407b' : '#bcd6ee',
          bgcolor: dragOver ? '#ebf4fb' : '#f8fbfe',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all .15s',
          '&:hover': disabled ? {} : { borderColor: '#03407b', bgcolor: '#f0f7fd' },
        }}
      >
        <input ref={inputRef} type="file" accept=".csv" hidden onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ''; }} />
        {file ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, borderRadius: '10px', bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
            <DescriptionOutlinedIcon sx={{ color: '#16a34a' }} />
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>{file.name}</Typography>
            <Box component="span" onClick={(e) => { e.stopPropagation(); setFile(null); }} sx={{ display: 'flex', color: '#94a3b8', '&:hover': { color: '#dc2626' } }}>
              <CloseIcon fontSize="small" />
            </Box>
          </Box>
        ) : (
          <>
            <CloudUploadOutlinedIcon sx={{ fontSize: 38, color: '#03407b' }} />
            <Typography sx={{ mt: 0.5, fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Glissez le fichier CSV ici ou cliquez pour le choisir · {hint}</Typography>
          </>
        )}
      </Box>

      {children}

      <LinkedinAccount />
      <BrowserCollectionHint />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          type="number"
          label={limitLabel}
          value={limit}
          disabled={disabled}
          onChange={(e) => setLimit(Math.max(0, Number(e.target.value)))}
          sx={{ width: 190 }}
          slotProps={{ htmlInput: { min: 0 } }}
        />
        <Typography sx={{ flex: 1, minWidth: 220, fontSize: '0.76rem', color: '#64748b' }}>
          0 = tout le fichier. Commencez petit (3) : les profils déjà traités ne sont pas recollectés.
        </Typography>
        <AppButton size="large" startIcon={<PlayArrowRoundedIcon />} disabled={!file || disabled} onClick={() => onLaunch(file, limit)}>
          {launchLabel}
        </AppButton>
      </Box>
    </Box>
  );
};

export const ImportCandidatesView = ({ op, onLaunch, onReset, onGoTo }) => (
  <Panel>
    <PanelHeader
      icon={<CloudUploadOutlinedIcon />}
      title="Importer des candidats depuis LinkedIn"
      subtitle="CSV multi-colonnes : la colonne des URL LinkedIn est détectée automatiquement."
    />
    {op.phase === 'idle' || (op.phase === 'error' && !op.jobId) ? (
      <>
        <CsvUpload
          title="Fichier CSV des profils"
          hint="une URL de profil LinkedIn par ligne"
          limitLabel="Profils à traiter"
          launchLabel="Extraire, valider et stocker"
          onLaunch={(file, limit) => onLaunch(file, 'extract_store', limit)}
        >
          <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, borderRadius: '10px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <ShieldOutlinedIcon sx={{ color: '#16a34a', fontSize: 20 }} />
            <Typography sx={{ fontSize: '0.78rem', color: '#166534', lineHeight: 1.5 }}>
              Chaque profil est collecté, structuré puis contrôlé : seuls les profils fiables sont stockés dans l'espace sourcing, isolé des candidats officiels du CRM.
            </Typography>
          </Box>
        </CsvUpload>
        {op.error && <Box sx={{ px: 2.5, pb: 2.5 }}>{isLinkedinBlocked(op.error) ? <LinkedinBlockedHelp detail={op.error} /> : <ErrorBanner>{op.error}</ErrorBanner>}</Box>}
      </>
    ) : (
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a', mb: 2 }}>
          {op.phase === 'running' ? `Import en cours : ${op.fileName}` : op.phase === 'done' ? 'Import terminé' : "L'import s'est arrêté"}
        </Typography>
        <PipelineSteps steps={op.steps} stepStates={op.stepStates} progress={op.progress} logs={op.logs} error={op.error} />
        {op.phase !== 'running' && (
          <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {op.phase === 'done' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#16a34a', mr: 'auto' }}>
                <CheckCircleOutlinedIcon fontSize="small" />
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>{op.results.length} profil(s) traité(s). Consultez les listes pour les statuts.</Typography>
              </Box>
            )}
            <AppButton variant="secondary" onClick={onReset}>Nouvel import</AppButton>
            <AppButton startIcon={<PeopleOutlineIcon />} onClick={() => onGoTo('stored')}>Voir les candidats</AppButton>
          </Box>
        )}
        {op.phase === 'running' && (
          <Typography sx={{ mt: 1.5, fontSize: '0.74rem', color: '#94a3b8' }}>Vous pouvez changer d'onglet : l'import continue en arrière-plan.</Typography>
        )}
      </Box>
    )}
  </Panel>
);

export const StoredCandidatesView = ({ snackbarShowMessage, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [crmLinks, setCrmLinks] = useState(readCrmLinks);
  const [toAdd, setToAdd] = useState(null);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  useEffect(() => {
    fetchAllCandidates('stored').then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const openCrm = (crmId) => window.open(`/candidats/${crmId}/view`, '_blank', 'noopener');

  const linkToCrm = (item, crmId) => {
    saveCrmLink(item.candidate_uuid, crmId);
    setCrmLinks(readCrmLinks());
  };

  const addOne = async (form) => {
    setAdding(true);
    try {
      const res = await addToCrm(form);
      linkToCrm(toAdd, res.id);
      snackbarShowMessage(res.updated ? 'Candidat déjà présent : fiche CRM mise à jour.' : 'Candidat ajouté au CRM.', 'success');
      setToAdd(null);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.detail || e.message;
      snackbarShowMessage(msg || "Échec de l'ajout au CRM.", 'error');
    } finally {
      setAdding(false);
    }
  };

  // Selection is keyed by LinkedIn URL; only candidates not yet in the CRM are added.
  const selectedItems = items.filter((x) => selected.includes(candidateUrl(x)));
  const pendingItems = selectedItems.filter((x) => !crmLinks[x.candidate_uuid]);

  // Each candidate is checked first: same LinkedIn -> existing record updated; same name only ->
  // left selected for a manual check (no risk of creating a duplicate).
  const addSelection = async () => {
    const total = pendingItems.length;
    let failed = 0;
    let added = 0;
    let updated = 0;
    const toReview = [];
    setBulkProgress({ done: 0, total, failed: 0 });
    for (let i = 0; i < total; i += 1) {
      const item = pendingItems[i];
      const form = toCrmForm(item);
      try {
        const matches = await checkCandidatDuplicate(form);
        const sameProfile = matches.find((m) => m.match === 'linkedin');
        if (!sameProfile && matches.length) {
          toReview.push(candidateUrl(item));
        } else {
          const res = await addToCrm(form);
          linkToCrm(item, res.id);
          if (res.updated || sameProfile) updated += 1; else added += 1;
        }
      } catch (e) {
        failed += 1;
      }
      setBulkProgress({ done: i + 1, total, failed });
    }
    const parts = [`${added} ajouté(s)`];
    if (updated) parts.push(`${updated} déjà présent(s), fiche mise à jour`);
    if (toReview.length) parts.push(`${toReview.length} doublon(s) possible(s) à vérifier`);
    if (failed) parts.push(`${failed} en échec`);
    snackbarShowMessage(`${parts.join(' · ')}.`, toReview.length || failed ? 'warning' : 'success', 6000);
    setBulkProgress(null);
    setBulkConfirm(false);
    // Possible duplicates stay selected: open each one with "Ajouter au CRM" to decide.
    setSelected(toReview);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteCandidate(toDelete.candidate_uuid, reason.trim());
      setItems((v) => v.filter((x) => x.candidate_uuid !== toDelete.candidate_uuid));
      snackbarShowMessage('Candidat supprimé du sourcing.', 'success');
      setToDelete(null);
      onChanged?.();
    } catch (e) {
      snackbarShowMessage(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <PanelHeader
        icon={<PeopleOutlineIcon />}
        tone="green"
        title="Candidats sourcés"
        subtitle="Profils validés et vectorisés : ajoutez-les au CRM ou utilisez-les pour le matching."
        action={selected.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <AppButton size="small" variant="ghost" onClick={() => setSelected([])}>Désélectionner</AppButton>
            <AppButton startIcon={<PersonAddAlt1OutlinedIcon />} disabled={!pendingItems.length} onClick={() => setBulkConfirm(true)}>
              Ajouter au CRM ({pendingItems.length})
            </AppButton>
          </Box>
        )}
      />
      {error && <Box sx={{ px: 2, pt: 2 }}><ErrorBanner>{error}</ErrorBanner></Box>}
      <CandidateBrowser
        items={items}
        loading={loading}
        selectable
        selected={selected}
        onToggle={(u) => setSelected((s) => (s.includes(u) ? s.filter((x) => x !== u) : [...s, u]))}
        onToggleAll={(urls, check) => setSelected((s) => (check ? [...new Set([...s, ...urls])] : s.filter((u) => !urls.includes(u))))}
        onDelete={(item) => { setToDelete(item); setReason(''); }}
        crmLinks={crmLinks}
        onAddToCrm={setToAdd}
        onOpenCrm={openCrm}
        emptyTitle="Aucun candidat sourcé"
        emptyText="Importez un CSV de profils LinkedIn pour alimenter le sourcing."
      />
      <AddToCrmDialog item={toAdd} busy={adding} onClose={() => setToAdd(null)} onSubmit={addOne} />
      <ConfirmDialog
        open={bulkConfirm}
        title={`Ajouter ${pendingItems.length} candidat(s) au CRM ?`}
        text={bulkProgress
          ? `Ajout en cours : ${bulkProgress.done}/${bulkProgress.total}${bulkProgress.failed ? ` (${bulkProgress.failed} en échec)` : ''}…`
          : "Chaque candidat est d'abord comparé au CRM : même LinkedIn = fiche existante mise à jour ; même nom = laissé sélectionné pour que vous vérifiiez. Les autres sont créés avec les informations extraites de LinkedIn."}
        confirmLabel="Ajouter au CRM"
        busy={!!bulkProgress}
        onConfirm={addSelection}
        onClose={() => setBulkConfirm(false)}
      />
      <ConfirmDialog
        open={!!toDelete}
        title="Supprimer ce candidat ?"
        text={`${toDelete ? candidateName(toDelete) : 'Ce candidat'} sera retiré du sourcing avec ses vecteurs. Une trace de la suppression est conservée.`}
        confirmLabel="Supprimer"
        danger
        busy={busy}
        withReason
        reason={reason}
        onReason={setReason}
        onConfirm={remove}
        onClose={() => setToDelete(null)}
      />
    </Panel>
  );
};

export const QueueView = ({ snackbarShowMessage, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyUrl, setBusyUrl] = useState('');

  const load = useCallback(() => fetchCandidates('ready_for_storage', 500).then((d) => setItems(d.items || [])), []);
  useEffect(() => { load().catch(() => {}).finally(() => setLoading(false)); }, [load]);

  const run = async (urls, single) => {
    if (single) setBusyUrl(urls[0]); else setBusy(true);
    try {
      await retryStorage(urls);
      setSelected([]);
      await load();
      snackbarShowMessage(`${urls.length} profil(s) relancé(s) pour le stockage.`, 'success');
      onChanged?.();
    } catch (e) {
      snackbarShowMessage(e.message, 'error');
    } finally {
      setBusy(false);
      setBusyUrl('');
    }
  };

  return (
    <Panel>
      <PanelHeader
        icon={<ReplayIcon />}
        title="File de relance"
        subtitle="Profils valides dont le stockage a échoué temporairement. La relance ne recollecte pas LinkedIn."
        action={
          <AppButton startIcon={<ReplayIcon />} loading={busy} disabled={loading || !items.length} onClick={() => run(selected.length ? selected : items.map(candidateUrl))}>
            {selected.length ? `Relancer (${selected.length})` : 'Tout relancer'}
          </AppButton>
        }
      />
      <CandidateBrowser
        items={items}
        loading={loading}
        selectable
        selected={selected}
        onToggle={(u) => setSelected((s) => (s.includes(u) ? s.filter((x) => x !== u) : [...s, u]))}
        onToggleAll={(urls, check) => setSelected((s) => (check ? [...new Set([...s, ...urls])] : s.filter((u) => !urls.includes(u))))}
        onRetry={(url) => run([url], true)}
        busyUrl={busyUrl}
        retryLabel="Relancer le stockage"
        showStoredAt={false}
        emptyTitle="File vide"
        emptyText="Aucun profil en attente de stockage."
      />
    </Panel>
  );
};

export const RejectionsView = ({ snackbarShowMessage, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUrl, setBusyUrl] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);

  const load = useCallback(
    () => Promise.all([fetchCandidates('not_storable', 500), fetchCandidates('extraction_failed', 500)])
      .then(([a, b]) => setItems([...(a.items || []), ...(b.items || [])])),
    [],
  );
  useEffect(() => { load().catch((e) => setError(e.message)).finally(() => setLoading(false)); }, [load]);

  const launch = async (url) => {
    const item = items.find((x) => candidateUrl(x) === url);
    const smart = item && Number(item.smart_retry_count || 0) === 0 && !hasLinkedinErrorNoise(item) && SMART_RETRY_REASONS.includes(item.reason_code);
    setPending(null);
    setError('');
    setMessage('');
    setBusyUrl(url);
    try {
      if (smart) {
        const result = await retrySmart([url]);
        const ok = (result.results || []).filter((x) => x.success).length;
        setMessage(ok ? 'Profil corrigé et stocké sans nouvelle collecte LinkedIn.' : "La correction ciblée n'a pas suffi : relancez une collecte.");
        snackbarShowMessage(ok ? 'Profil corrigé et stocké.' : 'Correction insuffisante.', ok ? 'success' : 'warning');
        await load();
        onChanged?.();
        setBusyUrl('');
        return;
      }
      const { job_id: jobId } = await retryScraping([url]);
      setMessage('Nouvelle collecte LinkedIn lancée : les statuts se mettront à jour à la fin.');
      snackbarShowMessage('Nouvelle collecte LinkedIn lancée.', 'info');
      subscribeEvents(jobId, async (event) => {
        if (event.type === 'error') setError(event.message);
        if (event.type === 'close') {
          await load();
          onChanged?.();
          setBusyUrl('');
          setMessage(event.status === 'done' ? 'Nouvelle extraction terminée, statuts actualisés.' : "La relance s'est terminée avec une erreur.");
        }
      });
    } catch (e) {
      setError(e.message);
      snackbarShowMessage(e.message, 'error');
      setBusyUrl('');
    }
  };

  return (
    <Panel>
      <PanelHeader
        icon={<ShieldOutlinedIcon />}
        tone="amber"
        title="Profils non stockés"
        subtitle="Refusés par le contrôle qualité ou en échec d'extraction. La relance choisit seule la reprise adaptée."
      />
      {(message || error) && (
        <Box sx={{ px: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {message && <Box sx={{ px: 2, py: 1.25, borderRadius: '10px', border: '1px solid #cfe0f1', bgcolor: '#f0f7fd', color: '#03407b', fontSize: '0.84rem' }}>{message}</Box>}
          <ErrorBanner>{error}</ErrorBanner>
        </Box>
      )}
      <CandidateBrowser
        items={items}
        loading={loading}
        onRetry={(url) => setPending(url)}
        busyUrl={busyUrl}
        retryLabel="Lancer la reprise adaptée"
        showStoredAt={false}
        emptyTitle="Aucun profil rejeté"
        emptyText="Tous les profils importés ont passé le contrôle qualité."
      />
      <ConfirmDialog
        open={!!pending}
        title="Relancer ce profil ?"
        text="Si les preuves déjà collectées suffisent, une correction ciblée est appliquée sans repasser par LinkedIn. Sinon, une nouvelle collecte est lancée (elle compte dans le quota journalier)."
        confirmLabel="Lancer la relance"
        onConfirm={() => launch(pending)}
        onClose={() => setPending(null)}
      />
    </Panel>
  );
};

export const HelpTile = ({ icon, title, text }) => (
  <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, borderRadius: '12px', border: '1px solid #e2edf7', bgcolor: '#f8fbfe' }}>
    <IconBox size={32}>{icon}</IconBox>
    <Box>
      <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.5 }}>{text}</Typography>
    </Box>
  </Box>
);
